import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import {
  Button,
  Checkbox,
  Drawer,
  Empty,
  Input,
  Modal,
  Popover,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { getRequest } from '../../services/base'
import { notifyError } from '../../utils/notifications'
import { NavContext } from '../../context/global_context'
import EllipsisTooltip from '../common/EllipsisTooltip'

// Highlights matching text in a string
const HighlightText = ({ text, query }) => {
  if (!query || !text) return text
  const q = query.toLowerCase()
  const lowerText = text.toLowerCase()
  const parts = []
  let lastIndex = 0
  let index = lowerText.indexOf(q)
  while (index !== -1) {
    if (index > lastIndex) {
      parts.push(
        <span key={`pre-${index}`}>{text.slice(lastIndex, index)}</span>
      )
    }
    parts.push(
      <mark key={`match-${index}`} className="harness-edit-highlight">
        {text.slice(index, index + query.length)}
      </mark>
    )
    lastIndex = index + query.length
    index = lowerText.indexOf(q, lastIndex)
  }
  if (lastIndex < text.length) {
    parts.push(<span key="rest">{text.slice(lastIndex)}</span>)
  }
  return parts
}

const HarnessEditPanel = ({
  visible,
  onClose,
  selectedHarnesses,
  checkedHarnessLevels,
  onSelectHarnessLevel,
  onAddHarness,
  onRemoveHarness,
  connectorViewName,
}) => {
  const { vehicle, connector_view } = useParams()
  const intl = useIntl()
  const [, dispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const [loading, setLoading] = useState(false)
  const [harnessesData, setHarnessesData] = useState([])
  const [checkedLevelIds, setCheckedLevelIds] = useState([])
  const [levelsInMultipleHarnesses, setLevelsInMultipleHarnesses] = useState([])
  const [selectedHarnessIds, setSelectedHarnessIds] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedLevels, setExpandedLevels] = useState(new Set())
  const [harnessOptions, setHarnessOptions] = useState([])
  const [harnessSearchLoading, setHarnessSearchLoading] = useState(false)
  const [harnessSearchFailed, setHarnessSearchFailed] = useState(false)
  const [addHarnessOpen, setAddHarnessOpen] = useState(false)
  const [selectedOptionCode, setSelectedOptionCode] = useState(null)
  const [allOptionCodes, setAllOptionCodes] = useState([])
  const harnessSearchTimer = useRef(null)
  const removedHarnessIdsRef = useRef(new Set())

  // Fetch summary data when panel opens, selectedHarnesses change, or sales code filter changes
  useEffect(() => {
    if (visible) {
      fetchSummary()
    }
  }, [visible, connector_view, selectedHarnesses, selectedOptionCode])

  // Reset removed harness IDs only when panel opens
  useEffect(() => {
    if (visible) {
      removedHarnessIdsRef.current = new Set()
    }
  }, [visible])

  // Sync checked levels from parent when panel opens or harnesses change
  useEffect(() => {
    if (visible) {
      setCheckedLevelIds([...checkedHarnessLevels])
      setSelectedHarnessIds(new Set(selectedHarnesses.map((h) => h.id)))
      setSelectedOptionCode(null)
    }
  }, [visible, selectedHarnesses])

  const fetchSummary = async () => {
    setLoading(true)
    try {
      // Fetch saved summary data from backend
      let savedHarnesses = []
      let savedCheckedIds = []
      let savedMultiHarness = []
      let savedOptionCodes = []

      if (connector_view) {
        let summaryUrl = `/api/connector_views/${connector_view}/summary/?vehicle=${vehicle}`
        if (selectedOptionCode)
          summaryUrl += `&option_code=${selectedOptionCode}`
        const res = await getRequest(summaryUrl)
        // base.js no longer rejects on a failed request, so the catch below only
        // sees genuine programming errors. Falling through here left every
        // `res?.x || []` on its empty default, which emptied the panel - it read
        // as "your harnesses were deleted" rather than "the fetch failed".
        if (res?.status === false) {
          notifyError(
            dispatch,
            intl.formatMessage({ id: 'connectorViews.summary.loadFailed' })
          )
          return
        }
        savedHarnesses = res?.harnesses || []
        savedCheckedIds = res?.checked_level_ids || []
        savedMultiHarness = res?.levels_in_multiple_harnesses || []
        savedOptionCodes = res?.option_codes || []
      }

      // Find harnesses in selectedHarnesses that aren't in the backend response
      const savedHarnessIds = new Set(savedHarnesses.map((h) => h.id))
      const unsavedHarnesses = selectedHarnesses.filter(
        (h) => !savedHarnessIds.has(h.id)
      )

      // Fetch levels for unsaved harnesses
      const unsavedHarnessesData = await Promise.all(
        unsavedHarnesses.map(async (harness) => {
          try {
            let unsavedUrl = `/api/harnesses/harness_levels/${harness.id}/?vehicle=${vehicle}&limit=500&offset=0`
            if (selectedOptionCode)
              unsavedUrl += `&option_code=${selectedOptionCode}`
            const res = await getRequest(unsavedUrl)
            // One request per harness, so a toast here would stack N deep.
            // Flag the failure instead and report the total once, below.
            const levels = res?.status === false ? [] : res?.levels || []
            // optionCodes from harness_levels is a top-level flat list [{id, name}]
            return {
              id: harness.id,
              harness_id: harness.harness_id,
              name: harness.harness_id,
              levels: levels,
              level_count: levels.length,
              topLevelOptionCodes: res?.optionCodes || [],
              failed: res?.status === false,
            }
          } catch {
            return {
              id: harness.id,
              harness_id: harness.harness_id,
              name: harness.harness_id,
              levels: [],
              level_count: 0,
              topLevelOptionCodes: [],
              failed: true,
            }
          }
        })
      )

      const failedCount = unsavedHarnessesData.filter((h) => h.failed).length
      if (failedCount) {
        notifyError(
          dispatch,
          intl.formatMessage(
            { id: 'connectorViews.summary.harnessLevelsLoadError' },
            { count: failedCount }
          )
        )
      }

      // Collect option codes from top-level response of unsaved harnesses
      const unsavedOptionCodes = {}
      unsavedHarnessesData.forEach((h) => {
        if (h.topLevelOptionCodes) {
          h.topLevelOptionCodes.forEach((oc) => {
            unsavedOptionCodes[oc.id] = oc.name
          })
        }
      })

      // Merge option codes from saved + unsaved
      const mergedOptionCodes = [...savedOptionCodes]
      Object.entries(unsavedOptionCodes).forEach(([id, name]) => {
        if (!mergedOptionCodes.some((oc) => oc.id === Number(id))) {
          mergedOptionCodes.push({ id: Number(id), name })
        }
      })

      const removedIds = removedHarnessIdsRef.current
      const filteredSaved = savedHarnesses.filter((h) => !removedIds.has(h.id))
      const filteredUnsaved = unsavedHarnessesData.filter(
        (h) => !removedIds.has(h.id)
      )
      setHarnessesData([...filteredSaved, ...filteredUnsaved])
      setCheckedLevelIds((prev) => [...new Set([...prev, ...savedCheckedIds])])
      setLevelsInMultipleHarnesses(savedMultiHarness)
      setAllOptionCodes(mergedOptionCodes)
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.summary.loadFailed' })
      )
    } finally {
      // Was a bare setLoading(false) after the try/catch, which the early return
      // in the guard above would have skipped - leaving the panel spinning.
      setLoading(false)
    }
  }

  // Filter harnesses by selection
  const visibleHarnesses = useMemo(
    () => harnessesData.filter((h) => selectedHarnessIds.has(h.id)),
    [harnessesData, selectedHarnessIds]
  )

  // Build a map of level_id -> list of harness names it belongs to
  const levelHarnessMap = useMemo(() => {
    const map = {}
    harnessesData.forEach((h) => {
      h.levels.forEach((level) => {
        if (!map[level.id]) map[level.id] = []
        map[level.id].push(h.name)
      })
    })
    return map
  }, [harnessesData])

  // Filter levels by search query (sales code filtering is done server-side via API)
  const filteredHarnesses = useMemo(() => {
    let result = visibleHarnesses

    // Apply text search filter
    if (!searchQuery.trim()) return result
    const q = searchQuery.toLowerCase()
    return result
      .map((h) => ({
        ...h,
        levels: h.levels.filter(
          (level) =>
            level.name.toLowerCase().includes(q) ||
            level.connector_names?.some((c) => c.toLowerCase().includes(q))
        ),
      }))
      .filter((h) => h.levels.length > 0)
  }, [visibleHarnesses, searchQuery])

  // Keep a ref to the latest filteredHarnesses to avoid stale closures in callbacks
  const filteredHarnessesRef = useRef(filteredHarnesses)
  useEffect(() => {
    filteredHarnessesRef.current = filteredHarnesses
  }, [filteredHarnesses])

  // Total visible levels count
  const totalVisibleLevels = useMemo(
    () => filteredHarnesses.reduce((sum, h) => sum + h.levels.length, 0),
    [filteredHarnesses]
  )

  // Checked count among visible levels
  const checkedCount = useMemo(() => {
    const visibleLevelIds = new Set()
    filteredHarnesses.forEach((h) =>
      h.levels.forEach((l) => visibleLevelIds.add(l.id))
    )
    return checkedLevelIds.filter((id) => visibleLevelIds.has(id)).length
  }, [filteredHarnesses, checkedLevelIds])

  // Toggle individual level
  const handleToggleLevel = useCallback(
    (levelId) => {
      onSelectHarnessLevel([levelId])
      setCheckedLevelIds((prev) => {
        const next = [...prev]
        const idx = next.indexOf(levelId)
        if (idx >= 0) {
          next.splice(idx, 1)
        } else {
          next.push(levelId)
        }
        return next
      })
    },
    [onSelectHarnessLevel]
  )

  // Select all visible levels (uses ref to avoid stale closure after harness toggle)
  const handleSelectAll = useCallback(() => {
    const current = filteredHarnessesRef.current
    const visibleLevelIds = []
    current.forEach((h) =>
      h.levels.forEach((l) => {
        if (!checkedLevelIds.includes(l.id)) {
          visibleLevelIds.push(l.id)
        }
      })
    )
    if (visibleLevelIds.length > 0) {
      onSelectHarnessLevel(visibleLevelIds, 'SELECT_ALL')
      setCheckedLevelIds((prev) => [...new Set([...prev, ...visibleLevelIds])])
    }
  }, [checkedLevelIds, onSelectHarnessLevel])

  // Deselect all visible levels (uses ref to avoid stale closure after harness toggle)
  const handleDeselectAll = useCallback(() => {
    const current = filteredHarnessesRef.current
    const visibleLevelIds = new Set()
    current.forEach((h) => h.levels.forEach((l) => visibleLevelIds.add(l.id)))
    // Remove visible level IDs from checked state
    setCheckedLevelIds((prev) => prev.filter((id) => !visibleLevelIds.has(id)))
    // Also remove from parent — use individual toggles for each visible level
    visibleLevelIds.forEach((id) => {
      if (checkedLevelIds.includes(id)) {
        onSelectHarnessLevel([id])
      }
    })
  }, [checkedLevelIds, onSelectHarnessLevel])

  // Toggle harness filter
  const handleToggleHarness = useCallback((harnessId) => {
    setSelectedHarnessIds((prev) => {
      const next = new Set(prev)
      if (next.has(harnessId)) {
        next.delete(harnessId)
      } else {
        next.add(harnessId)
      }
      return next
    })
  }, [])

  // Toggle level expansion
  const handleToggleExpand = useCallback((levelId) => {
    setExpandedLevels((prev) => {
      const next = new Set(prev)
      if (next.has(levelId)) {
        next.delete(levelId)
      } else {
        next.add(levelId)
      }
      return next
    })
  }, [])

  // Fetch harnesses for the add-harness Select
  const fetchHarnessOptions = useCallback(
    async (search = '') => {
      setHarnessSearchLoading(true)
      setHarnessSearchFailed(false)
      try {
        let url = `/api/harnesses/?vehicle=${vehicle}&limit=50&offset=0`
        if (search.trim()) url += `&search=${search}`
        const res = await getRequest(url)
        // This fires on every keystroke, so the failure is shown inside the
        // dropdown rather than as a toast - see notFoundContent below.
        if (res?.status === false) {
          setHarnessSearchFailed(true)
          setHarnessOptions([])
          return
        }
        const results = res?.results || []
        setHarnessOptions(
          results
            .filter((h) => !selectedHarnesses.some((s) => s.id === h.id))
            .map((h) => ({ value: h.id, label: h.harness_id, harness: h }))
        )
      } catch {
        setHarnessSearchFailed(true)
        setHarnessOptions([])
      } finally {
        setHarnessSearchLoading(false)
      }
    },
    [vehicle, selectedHarnesses]
  )

  const handleHarnessSearch = useCallback(
    (value) => {
      if (harnessSearchTimer.current) clearTimeout(harnessSearchTimer.current)
      harnessSearchTimer.current = setTimeout(() => {
        fetchHarnessOptions(value || '')
      }, 300)
    },
    [fetchHarnessOptions]
  )

  // Confirm and add harness
  const handleConfirmAddHarness = useCallback(
    (harnessId) => {
      const option = harnessOptions.find((o) => o.value === harnessId)
      if (!option) return
      setAddHarnessOpen(false)
      modal.confirm({
        title: intl.formatMessage({
          id: 'connectorViews.summary.addHarnessConfirmTitle',
        }),
        content: intl.formatMessage(
          { id: 'connectorViews.summary.addHarnessConfirmContent' },
          { name: option.label }
        ),
        okText: intl.formatMessage({
          id: 'connectorViews.summary.addHarnessConfirmOk',
        }),
        cancelText: intl.formatMessage({
          id: 'connectorViews.summary.addHarnessConfirmCancel',
        }),
        onOk: () => {
          onAddHarness(option.harness)
          setHarnessOptions([])
        },
      })
    },
    [harnessOptions, onAddHarness, intl]
  )

  // Auto-expand levels whose connector names match the search query (but level name doesn't)
  const autoExpandedLevelIds = useMemo(() => {
    if (!searchQuery.trim()) return new Set()
    const q = searchQuery.toLowerCase()
    const ids = new Set()
    filteredHarnesses.forEach((h) =>
      h.levels.forEach((level) => {
        const levelNameMatches = level.name.toLowerCase().includes(q)
        const connectorNameMatches = level.connector_names?.some((c) =>
          c.toLowerCase().includes(q)
        )
        if (connectorNameMatches && !levelNameMatches) {
          ids.add(level.id)
        }
      })
    )
    return ids
  }, [filteredHarnesses, searchQuery])

  // Merged expanded state: manual toggles + auto-expanded from search
  const effectiveExpandedLevels = useMemo(() => {
    const merged = new Set(expandedLevels)
    autoExpandedLevelIds.forEach((id) => merged.add(id))
    return merged
  }, [expandedLevels, autoExpandedLevelIds])

  const isAllChecked =
    totalVisibleLevels > 0 && checkedCount === totalVisibleLevels

  const handleDrawerClose = useCallback(() => {
    modal.confirm({
      title: intl.formatMessage({
        id: 'connectorViews.summary.unsavedWarningTitle',
      }),
      content: intl.formatMessage({
        id: 'connectorViews.summary.unsavedWarningContent',
      }),
      okText: intl.formatMessage({ id: 'connectorViews.summary.closePanel' }),
      cancelText: intl.formatMessage({ id: 'connectorViews.summary.goBack' }),
      onOk: onClose,
    })
  }, [onClose, intl])

  return (
    <Drawer
      title={`${intl.formatMessage({ id: 'connectorViews.summary.title' })}${connectorViewName ? ` - ${connectorViewName}` : ''}`}
      open={visible}
      onClose={handleDrawerClose}
      width={900}
      placement="right"
      className="harness-edit-panel"
      maskClosable={false}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
      }}
    >
      {contextHolder}
      {loading && harnessesData.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', flex: 1 }}>
          <Spin />
          <div style={{ marginTop: 12 }}>
            <Typography.Text type="secondary">
              {intl.formatMessage({ id: 'connectorViews.summary.loading' })}
            </Typography.Text>
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Left Panel — Harness Filter */}
            <div className="harness-edit-filter">
              <div className="harness-edit-filter-header">
                <Typography.Text
                  className='bold'
                  style={{
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {intl.formatMessage({
                    id: 'connectorViews.summary.harnessFilter',
                  })}
                </Typography.Text>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={addHarnessOpen}
                  onOpenChange={(open) => {
                    setAddHarnessOpen(open)
                    if (open) {
                      fetchHarnessOptions()
                    } else {
                      setHarnessOptions([])
                    }
                  }}
                  content={
                    <div
                      style={{ width: 350 }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Typography.Text
                        className='bold'
                        style={{
                          fontSize: 12,
                          display: 'block',
                          marginBottom: 16,
                        }}
                      >
                        {intl.formatMessage({
                          id: 'connectorViews.form.addHarness',
                        })}
                      </Typography.Text>
                      <Select
                        showSearch
                        data-testid="vv-harness-select"
                        placeholder={intl.formatMessage({
                          id: 'connectorViews.harnesses.enterName',
                        })}
                        style={{ width: '100%' }}
                        filterOption={false}
                        onSearch={handleHarnessSearch}
                        onChange={handleConfirmAddHarness}
                        loading={harnessSearchLoading}
                        options={harnessOptions}
                        notFoundContent={
                          harnessSearchLoading ? (
                            <Spin size="small" />
                          ) : harnessSearchFailed ? (
                            <Typography.Text type="danger" style={{color: 'var(--color-form-link-danger)'}}>
                              {intl.formatMessage({
                                id: 'connectorViews.harnesses.searchFailed',
                              })}
                            </Typography.Text>
                          ) : null
                        }
                        value={null}
                        autoFocus
                        getPopupContainer={(trigger) => trigger.parentElement}
                      />
                    </div>
                  }
                >
                <Button type="link" className='link-button' size="small" icon={<PlusOutlined />} style={{ fontSize: 12}} data-testid="vv-add-harness">
                  {intl.formatMessage({ id: 'connectorViews.form.addHarness',})}
                </Button>
                </Popover>
              </div>
              <div className="harness-edit-harness-list">
                {harnessesData
                  .filter((h) => !removedHarnessIdsRef.current.has(h.id))
                  .map((h) => (
                    <div key={h.id} className="harness-edit-harness-item" data-testid="vv-harness-item" data-name={h.name}>
                      <Checkbox
                        checked={selectedHarnessIds.has(h.id)}
                        onChange={() => handleToggleHarness(h.id)}
                      />
                      <Typography.Text
                        className="harness-edit-harness-name"
                        ellipsis
                      >
                        <EllipsisTooltip className='ellipsis'>{h.name}</EllipsisTooltip>
                      </Typography.Text>
                      <Typography.Text
                        type="secondary"
                        className="harness-edit-harness-count"
                      >
                        {h.level_count}
                      </Typography.Text>
                      {onRemoveHarness && (
                          <DeleteOutlined
                            className="harness-edit-harness-remove"
                            onClick={(e) => {
                              e.stopPropagation()
                              modal.confirm({
                                title: intl.formatMessage({
                                  id: 'connectorViews.summary.removeHarnessConfirmTitle',
                                }),
                                content: intl.formatMessage(
                                  {
                                    id: 'connectorViews.summary.removeHarnessConfirmContent',
                                  },
                                  { name: h.name }
                                ),
                                okText: intl.formatMessage({
                                  id: 'connectorViews.summary.removeHarnessConfirmOk',
                                }),
                                okType: 'danger',
                                cancelText: intl.formatMessage({
                                  id: 'connectorViews.summary.removeHarnessConfirmCancel',
                                }),
                                onOk: () => {
                                  removedHarnessIdsRef.current.add(h.id)
                                  onRemoveHarness(h, h.levels || [])
                                },
                              })
                            }}
                          />
                      )}
                    </div>
                  ))}
                {harnessesData.length === 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, padding: '0 6px' }}>
                    {intl.formatMessage({
                      id: 'connectorViews.summary.noHarnesses',
                    })}
                  </Typography.Text>
                )}
              </div>
            </div>

            {/* Right Panel — Levels & Connectors */}
            <div className="harness-edit-levels-panel">
              {/* Search + Sales Code Filter (same row) */}
              <div
                className="harness-edit-search"
              >
                <Input.Search
                  size='small'
                  data-testid="vv-level-search"
                  placeholder={intl.formatMessage({
                    id: 'connectorViews.summary.searchPlaceholder',
                  })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={(val) => setSearchQuery(val)}
                  allowClear
                  enterButton={false}
                  style={{ flex: 1, minWidth: 0 }}
                />
                {allOptionCodes.length > 0 && (
                  <Select
                    size='small'
                    allowClear
                    showSearch
                    placeholder={intl.formatMessage({
                      id: 'connectorViews.summary.salesCodeFilter',
                    })}
                    style={{ width: 220 }}
                    value={selectedOptionCode}
                    onChange={(val) => setSelectedOptionCode(val ?? null)}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={allOptionCodes.map((oc) => ({
                      value: oc.id,
                      label: oc.name,
                    }))}
                  />
                )}
              </div>

              {/* Actions bar */}
              <div className="harness-edit-actions">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Button
                    size='small'
                    onClick={handleSelectAll}
                    disabled={totalVisibleLevels === 0}
                    data-testid="vv-select-all-levels"
                  >
                    {intl.formatMessage({
                      id: 'connectorViews.summary.selectAll',
                    })}
                  </Button>
                  <Button
                    size='small'
                    onClick={handleDeselectAll}
                    disabled={totalVisibleLevels === 0}
                    data-testid="vv-deselect-all-levels"
                  >
                    {intl.formatMessage({
                      id: 'connectorViews.summary.deselectAll',
                    })}
                  </Button>
                  {checkedCount > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {intl.formatMessage(
                        { id: 'connectorViews.summary.selected' },
                        { count: checkedCount }
                      )}
                    </Typography.Text>
                  )}
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {intl.formatMessage(
                    { id: 'connectorViews.summary.showingLevels' },
                    {
                      count: totalVisibleLevels,
                      total: harnessesData.reduce(
                        (sum, h) => sum + h.level_count,
                        0
                      ),
                    }
                  )}
                </Typography.Text>
              </div>

              {/* Levels grouped by harness */}
              <div
                className="harness-edit-levels-list"
                style={{ position: 'relative' }}
              >
                {loading && harnessesData.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.6)',
                      zIndex: 10,
                    }}
                  >
                    <Spin />
                  </div>
                )}
                {filteredHarnesses.length === 0 ? (
                  <Empty
                    description={intl.formatMessage({
                      id: 'connectorViews.summary.noLevels',
                    })}
                    style={{ padding: '40px 0' }}
                  />
                ) : (
                  filteredHarnesses.map((harness) => (
                    <div key={harness.id} className="harness-edit-section">
                      <div className="harness-edit-section-header">
                        <Typography.Text
                          style={{ fontSize: 12 }}
                          className="bolder"
                        >
                          {harness.name}
                        </Typography.Text>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 11 }}
                        >
                          {harness.levels.length}{' '}
                          {intl.formatMessage({
                            id: 'connectorViews.summary.levels',
                          })}
                        </Typography.Text>
                      </div>
                      {harness.levels.map((level) => {
                        const isChecked = checkedLevelIds.includes(level.id)
                        const isExpanded = effectiveExpandedLevels.has(level.id)
                        const harnessNames = levelHarnessMap[level.id] || []
                        const inMultipleHarnesses = harnessNames.length > 1

                        return (
                          <div
                            key={level.id}
                            className="harness-edit-level-item"
                            data-testid="vv-level-item"
                          >
                            <div className="harness-edit-level-row">
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleToggleLevel(level.id)}
                              >
                                <Typography.Text style={{ fontSize: 13 }}>
                                  <HighlightText
                                    text={level.name}
                                    query={searchQuery}
                                  />
                                </Typography.Text>
                              </Checkbox>
                              <div className="harness-edit-level-badges">
                                {inMultipleHarnesses &&
                                  harnessNames.map((name) => (
                                    <Tag
                                      key={name}
                                      color={
                                        name === harness.name
                                          ? 'blue'
                                          : 'default'
                                      }
                                      style={{
                                        fontSize: 11,
                                        marginInlineEnd: 2,
                                      }}
                                    >
                                      {name}
                                    </Tag>
                                  ))}
                              </div>
                              {level.connector_names?.length > 0 && (
                                <Button
                                  type="link"
                                  className={`harness-edit-connectors-toggle link-button ${isExpanded ? 'neutral' : ''}`}
                                  onClick={() => handleToggleExpand(level.id)}
                                  icon={
                                    isExpanded ? (
                                      <DownOutlined />
                                    ) : (
                                      <RightOutlined />
                                    )
                                  }
                                >
                                  {intl.formatMessage(
                                    { id: 'connectorViews.summary.connectors' },
                                    { count: level.connector_names.length }
                                  )}
                                </Button>
                              )}
                            </div>
                            {isExpanded &&
                              level.connector_names?.length > 0 && (
                                <div className="harness-edit-connectors-list">
                                  {level.connector_names.map((name, idx) => {
                                    return (
                                      <Tag key={`${level.id}-conn-${idx}`}>
                                        <HighlightText
                                          text={name}
                                          query={searchQuery}
                                        />
                                      </Tag>
                                    )
                                  })}
                                </div>
                              )}
                          </div>
                        )
                      })}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Footer with Proceed button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '12px 12px',
              borderTop: '1px solid var(--color-border-custom)',
              flexShrink: 0,
            }}
          >
            <Button
              type="primary"
              size='small'
              data-testid="vv-proceed"
              onClick={() => {
                modal.confirm({
                  title: intl.formatMessage({
                    id: 'connectorViews.summary.unsavedWarningTitle',
                  }),
                  content: intl.formatMessage({
                    id: 'connectorViews.summary.unsavedWarningContent',
                  }),
                  okText: intl.formatMessage({
                    id: 'connectorViews.summary.closePanel',
                  }),
                  cancelText: intl.formatMessage({
                    id: 'connectorViews.summary.goBack',
                  }),
                  onOk: onClose,
                })
              }}
            >
              {intl.formatMessage({ id: 'connectorViews.summary.proceed' })}
            </Button>
          </div>
        </>
      )}
    </Drawer>
  )
}

export default HarnessEditPanel
